/**
 * Which ATW systems a person may open, and what they can reach inside each one.
 *
 * - One, Hub, and WorkSuite (stored id `workspaces`) have no per-user app entitlement, so their
 *   grant is stored on the ATW user as `systemAccess` (UserProfile + per-org MembershipProfile).
 * - Pay, the separate `worksuite` app entitlement, and Platform already are app entitlements
 *   (`appEntitlements`); the Directory toggles those slugs directly instead of storing a second copy.
 * - Inside One, access is the existing One app entitlements (CRM, Finance, …).
 *
 * Hub (hub.atw.net) signs in via the Auth hub: Directory `hub` grant is checked when minting
 * `/api/hub/sso-ticket`, then Lightsail exchanges the ticket for a Hub Bearer session.
 *
 * WorkSuite shell grant id is stored as `workspaces` (Mail + Calendar + Meet on
 * worksuite.atw.net). Legacy stored grant `mail` is accepted on read and normalized to
 * `workspaces` on write. The separate `worksuite` app entitlement remains for integrations.
 */

import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import { localDevPortSuffix } from "@/lib/atwOneHost";




/** Canonical stored system ids (Directory toggles + membership.systemAccess). */
export type AtwStoredSystemId = "one" | "hub" | "workspaces" | "agents";

/** Raw values that may still appear in Dynamo from before the WorkSuite rename. */
export type AtwLegacyStoredSystemId = AtwStoredSystemId | "mail";

export type AtwSystemId = AtwStoredSystemId | "pay" | "worksuite" | "platform";

export const ATW_STORED_SYSTEM_IDS: AtwStoredSystemId[] = ["one", "hub", "workspaces", "agents"];

/** One apps shown as "inside One" (Pay / WorkSuite / Platform are their own systems). */
export const ONE_INNER_APP_SLUGS: Exclude<GlobalAppSlug, "home">[] = [
  "crm",
  "finance",
  "human-resources",
  "marketing",
  "knowledge",
  "projects",
  "analytics",
  "custom-apps",
  "support",
];

export type AtwSystemDefinition = {
  id: AtwSystemId;
  name: string;
  summary: string;
  publicHost?: string;
  /** Where the grant lives. */
  storage: { kind: "systemAccess" } | { kind: "appEntitlement"; slug: GlobalAppSlug };
  /** App entitlements that scope access inside this system. */
  innerAppSlugs?: Exclude<GlobalAppSlug, "home">[];
  /** Shown when the system has no in-product scopes managed here. */
  innerNote?: string;
};

export const ATW_SYSTEMS: AtwSystemDefinition[] = [
  {
    id: "one",
    name: "ATW One",
    summary: "CRM, finance, HR, marketing, knowledge, projects, analytics, support, and custom apps.",
    publicHost: "one.atw.net",
    storage: { kind: "systemAccess" },
    innerAppSlugs: ONE_INNER_APP_SLUGS,
  },
  {
    id: "hub",
    name: "ATW Hub",
    summary: "Team chat and collaboration.",
    publicHost: "hub.atw.net",
    storage: { kind: "systemAccess" },
    innerNote: "Channels and Hub roles are managed inside Hub.",
  },
  {
    id: "workspaces",
    name: "ATW WorkSuite",
    summary: "Mail, calendar, and meetings for your organization.",
    publicHost: "worksuite.atw.net",
    storage: { kind: "systemAccess" },
    innerNote: "Mailboxes and aliases are managed in Mail admin. Calendar and Meet share this grant.",
  },
  {
    id: "agents",
    name: "ATW Agents",
    summary: "Hire named AI seats, configure agents, and run Overseer for your organization.",
    publicHost: "agents.atw.net",
    storage: { kind: "systemAccess" },
    innerNote: "Seats, Agent Configuration, Overseer, and credits live in Agents.",
  },
  {
    id: "pay",
    name: "ATW Pay",
    summary: "Payments, API keys, and checkout.",
    publicHost: "pay.atw.net",
    storage: { kind: "appEntitlement", slug: "pay" },
    innerNote: "Payments, API keys, and Checkout.",
  },
  {
    id: "worksuite",
    name: "WorkSuite",
    summary: "Email, calendar, files, meetings, and messaging integrations.",
    storage: { kind: "appEntitlement", slug: "worksuite" },
  },
  {
    id: "platform",
    name: "Platform",
    summary: "ATW operator console (ATW staff only).",
    storage: { kind: "appEntitlement", slug: "platform" },
  },
];

const CANONICAL = new Set<string>(ATW_STORED_SYSTEM_IDS);

/** Map legacy `mail` → `workspaces`; drop unknown ids. */
export function normalizeStoredSystemId(raw: string): AtwStoredSystemId | null {
  const id = raw.trim().toLowerCase();
  if (id === "mail") return "workspaces";
  if (CANONICAL.has(id)) return id as AtwStoredSystemId;
  return null;
}

export function sanitizeStoredSystemAccess(raw: unknown): AtwStoredSystemId[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = new Set<AtwStoredSystemId>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const id = normalizeStoredSystemId(item);
    if (id) out.add(id);
  }
  return ATW_STORED_SYSTEM_IDS.filter((id) => out.has(id));
}

/**
 * Stored system grants, or defaults when never configured: One + Agents for everyone,
 * WorkSuite (`workspaces`) when the org has Mail enabled. Hub is opt-in.
 */
export function effectiveStoredSystemAccess(
  systemAccess: AtwLegacyStoredSystemId[] | string[] | null | undefined,
  opts: { orgMailEnabled?: boolean },
): AtwStoredSystemId[] {
  if (Array.isArray(systemAccess)) {
    return sanitizeStoredSystemAccess(systemAccess) ?? [];
  }
  return opts.orgMailEnabled ? ["one", "workspaces", "agents"] : ["one", "agents"];
}

/** True when the person may open WorkSuite (canonical `workspaces` or legacy `mail` grant). */
export function hasWorkspaceSystemAccess(granted: readonly string[] | null | undefined): boolean {
  if (!granted?.length) return false;
  return granted.some((g) => normalizeStoredSystemId(g) === "workspaces");
}

/** True when `granted` includes `system` (WorkSuite accepts legacy `mail`). */
export function hasStoredSystemGrant(
  granted: readonly string[] | null | undefined,
  system: string,
): boolean {
  const want = normalizeStoredSystemId(system) ?? system.trim().toLowerCase();
  if (want === "workspaces") return hasWorkspaceSystemAccess(granted);
  if (!granted?.length) return false;
  return granted.some((g) => (normalizeStoredSystemId(g) ?? g.trim().toLowerCase()) === want);
}

function isLocalDevSystemLaunch(): boolean {
  if (typeof window !== "undefined") {
    const h = window.location.hostname.toLowerCase();
    return h === "localhost" || h === "127.0.0.1" || h.endsWith(".localhost");
  }
  return import.meta.env.DEV;
}

function atwHubAppUrl(): string {
  if (isLocalDevSystemLaunch()) {
    return `http://hub.localhost${localDevPortSuffix()}/`;
  }
  return "https://hub.atw.net/";
}

/**
 * Public launch URL for an enabled Directory system. Returns null when the product has no
 * public host (WorkSuite / Platform) or the id is unknown.
 */
export function atwSystemLaunchUrl(systemId: AtwSystemId | string): string | null {
  switch (systemId) {
    case "one":
      return "https://one.atw.net/";
    case "workspaces":
    case "mail":
      return "https://mail.atw.net/inbox";
    case "hub":
      return atwHubAppUrl();
    case "agents":
      return "https://agents.atw.net/";
    case "pay":
      return "https://pay.atw.net/";
    case "worksuite":
    case "platform":
      return null;
    default:
      return null;
  }
}
